import { OrderSummaryProps } from '../../types';

export function OrderSummary({
  cartItems,
  subtotal,
  quantityDiscount,
  promoDiscount,
  handlingFee,
  pickPackFee,
  shippingCost,
  tax,
  total,
  isVirtualOnlyCart = false,
  canSubmit,
  isSubmitting = false,
  paymentError,
  submissionError,
  onSubmit,
}: OrderSummaryProps) {
  const handlingFees = handlingFee + pickPackFee;
  return (
    <div className="bg-white rounded-lg shadow-sm p-6 lg:sticky lg:top-8">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h2>

      {/* Cart Items */}
      <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
        {cartItems.map((item) => {
          const discountTier = item.product.quantityDiscounts
            ?.filter(t => item.quantity >= t.minQuantity && (t.maxQuantity === null || item.quantity <= t.maxQuantity))
            .sort((a, b) => b.minQuantity - a.minQuantity)[0];
          const discountedUnitPrice = discountTier ? discountTier.customPrice : item.variation.price;
          const secondLogoFee = item.secondLogoFee ?? 0;
          const customerChargesTotal = item.customerCharges?.reduce((sum, c) => sum + c.amount, 0) ?? 0;
          const lineTotal = (discountedUnitPrice + secondLogoFee) * item.quantity + customerChargesTotal;

          return (
            <div key={item.id} className="flex items-center space-x-3 text-sm">
              <img
                src={item.product.images[0]}
                alt={item.product.name}
                className="w-12 h-12 object-cover rounded"
              />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">{item.product.name}</p>
                <p className="text-gray-500">
                  {item.variation.color} × {item.quantity}
                  {item.logoOptionName && <span className="block text-xs">Logo: {item.logoOptionName}</span>}
                  {item.secondLogoOptionName && (
                    <span className="block text-xs">2nd Logo: {item.secondLogoOptionName}{secondLogoFee > 0 && ` (+$${secondLogoFee.toFixed(2)})`}</span>
                  )}
                  {item.customerCharges?.map((charge, i) => (
                    <span key={i} className="block text-xs">{charge.name || 'One Time Charge'}: ${charge.amount.toFixed(2)}</span>
                  ))}
                </p>
                {discountTier && (
                  <span className="inline-block text-xs bg-green-100 text-green-700 rounded px-1 mt-0.5">
                    Qty Price
                  </span>
                )}
              </div>
              <div className="text-right">
                {discountTier && (
                  <p className="text-xs text-gray-400 line-through">
                    ${(item.variation.price * item.quantity).toFixed(2)}
                  </p>
                )}
                <p className="font-medium text-gray-900">${lineTotal.toFixed(2)}</p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="border-t border-gray-200 pt-4 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Subtotal</span>
          <span className="font-medium text-gray-900">${(subtotal + quantityDiscount).toFixed(2)}</span>
        </div>

        {handlingFees !== 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Handling Fees</span>
            <span className="font-medium text-gray-900">${handlingFees.toFixed(2)}</span>
          </div>
        )}

        {quantityDiscount > 0 && (
          <div className="flex justify-between text-sm text-green-600">
            <span>Quantity Discount</span>
            <span>-${quantityDiscount.toFixed(2)}</span>
          </div>
        )}

        {!isVirtualOnlyCart && shippingCost !== null && (
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Shipping</span>
            <span className="font-medium text-gray-900">
              ${shippingCost.toFixed(2)}
            </span>
          </div>
        )}

        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Tax</span>
          <span className="font-medium text-gray-900">${tax.toFixed(2)}</span>
        </div>

        {promoDiscount > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Total before discount</span>
            <span className="font-medium text-gray-900">${(subtotal + handlingFees + (shippingCost ?? 0) + tax).toFixed(2)}</span>
          </div>
        )}

        {promoDiscount > 0 && (
          <div className="flex justify-between text-sm text-green-600">
            <span>Promo Discount</span>
            <span>-${promoDiscount.toFixed(2)}</span>
          </div>
        )}

        <div className="border-t border-gray-200 pt-2 flex justify-between">
          <span className="text-base font-semibold text-gray-900">Total</span>
          <span className="text-base font-semibold text-gray-900">${total.toFixed(2)}</span>
        </div>
      </div>

      {paymentError && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800">{paymentError}</p>
        </div>
      )}

      {submissionError && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800">{submissionError}</p>
        </div>
      )}

      <button
        type="button"
        onClick={onSubmit}
        disabled={!canSubmit || isSubmitting}
        className={`w-full mt-6 px-6 py-3 rounded-lg font-semibold transition-all ${
          canSubmit && !isSubmitting
            ? 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800'
            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
        }`}
      >
        {isSubmitting ? (
          <span className="flex items-center justify-center">
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Processing...
          </span>
        ) : (
          'Place Order'
        )}
      </button>
    </div>
  );
}
